/**
 * admin/makua/AdminBookingSettings.js — money and holds (spec 05).
 * Nothing about deposits, deadlines or methods is assumed by the code;
 * it is all set here, and the booking pages offer only what's switched on.
 */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { bookingConfigAPI } from "../../services/api";
import { useLoad, Field, Badge } from "./ops";
import "./ops.css";

export default function AdminBookingSettings() {
  const { data, error, reload } = useLoad(() => bookingConfigAPI.get(), []);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!data) return;
    setF({
      currency: data.currency, deposit_mode: data.deposit_mode, deposit_value: data.deposit_value,
      balance_due_days: data.balance_due_days ?? "", plan_enabled: !!data.plan_enabled, plan_installments: data.plan_installments,
      methods: data.methods_listed || ["bank"], bank_instructions: data.bank_instructions || "", hold_hours: data.hold_hours,
      same_day_turnover: !!data.same_day_turnover, screening_note: data.screening_note || "", cancellation_summary: data.cancellation_summary || "",
    });
  }, [data]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleMethod = (m) => setF({ ...f, methods: f.methods.includes(m) ? f.methods.filter((x) => x !== m) : [...f.methods, m] });
  const save = async () => {
    setSaving(true);
    try { await bookingConfigAPI.save({ ...f, balance_due_days: f.balance_due_days === "" ? null : f.balance_due_days }); toast.success("Saved."); reload(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <AdminLayout pageTitle="Booking settings" pageSubtitle="Deposits, balance dates, instalments, payment methods and holds." permission="settings.view" feature="reservations">
      <div className="ops" style={{ maxWidth: "60rem" }}>
        {error && <p className="ops-error">{error}</p>}
        {f && (
          <>
            <div className="ops-card">
              <h3>What guests pay when they book</h3>
              <div className="ops-grid ops-grid--3">
                <Field label="Deposit">
                  <select value={f.deposit_mode} onChange={set("deposit_mode")}><option value="none">No deposit: pay in full</option><option value="percent">Percentage of the total</option><option value="fixed">Fixed amount per guest</option></select>
                </Field>
                {f.deposit_mode !== "none" && <Field label={f.deposit_mode === "percent" ? "Percent" : "Per guest (USD)"}><input type="number" min="0" value={f.deposit_value} onChange={set("deposit_value")} /></Field>}
                <Field label="Balance due, days before arrival" hint="Needed for deposits and instalments. Bookings closer than this pay in full."><input type="number" min="0" value={f.balance_due_days} onChange={set("balance_due_days")} /></Field>
                <label className="ops-check"><input type="checkbox" checked={f.plan_enabled} onChange={set("plan_enabled")} />Offer instalments</label>
                {f.plan_enabled && <Field label="Number of instalments"><input type="number" min="2" max="12" value={f.plan_installments} onChange={set("plan_installments")} /></Field>}
                <Field label="Currency"><input value={f.currency} onChange={set("currency")} maxLength={3} /></Field>
              </div>
            </div>

            <div className="ops-card">
              <h3>How they can pay</h3>
              <div className="ops-grid ops-grid--3">
                {[["bank", "Bank transfer", true], ["card", "Card", !!data.card_provider], ["crypto", "Cryptocurrency", !!data.crypto_provider]].map(([m, l, live]) => (
                  <label className="ops-check" key={m}>
                    <input type="checkbox" checked={f.methods.includes(m)} onChange={() => toggleMethod(m)} />{l}
                    {!live && <Badge tone="amber">{m === "card" ? "Add Stripe keys in Settings" : "No processor connected"}</Badge>}
                  </label>
                ))}
              </div>
              <p className="ops-note" style={{ marginTop: "0.6rem" }}>
                Card is offered once Makua's Stripe keys are saved in Settings; guests pay on Stripe's page and the reservation confirms itself. Crypto waits for its processor. Bank transfers are recorded by staff on the reservation.
              </p>
              <Field label="Bank transfer details, emailed with each reservation"><textarea rows={4} value={f.bank_instructions} onChange={set("bank_instructions")} placeholder={"Bank name\nAccount name\nAccount number / IBAN\nSWIFT"} /></Field>
            </div>

            <div className="ops-card">
              <h3>Holds and rooms</h3>
              <div className="ops-grid ops-grid--2">
                <Field label="Hours an unpaid reservation keeps its room" hint="After this the room and place go back on sale."><input type="number" min="1" max="720" value={f.hold_hours} onChange={set("hold_hours")} /></Field>
                <label className="ops-check"><input type="checkbox" checked={f.same_day_turnover} onChange={set("same_day_turnover")} />A room can be checked out and in on the same day</label>
              </div>
            </div>

            <div className="ops-card">
              <h3>What guests read</h3>
              <Field label="Medical screening note (booking page)"><textarea value={f.screening_note} onChange={set("screening_note")} placeholder="Every guest completes a short, confidential medical screening after booking…" /></Field>
              <Field label="Cancellation summary (review step)" hint="One or two sentences; the full policy stays on its own page."><textarea value={f.cancellation_summary} onChange={set("cancellation_summary")} /></Field>
            </div>
            <div className="ops-actions"><button className="ops-btn ops-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save settings"}</button></div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
